<template>
  <div ref="chartContainer" class="d3-chart-container relative group">
    <div class="absolute top-2 right-2 flex flex-wrap gap-3 z-10">
      <div
        v-for="s in [...barSeries, ...lineSeries]"
        :key="s.valueField"
        class="flex items-center gap-1.5 cursor-pointer select-none transition-opacity"
        :class="{ 'opacity-40': hiddenSeries.has(s.valueField) }"
        @click="toggleSeries(s.valueField)"
      >
        <span class="w-3 h-3 rounded-full" :style="{ backgroundColor: s.color }"></span>
        <span class="text-xs font-medium text-gray-600 dark:text-gray-400">{{ s.name }}</span>
      </div>
    </div>
    <svg ref="svgRef"></svg>
    <div
      v-show="tooltipVisible"
      ref="tooltipRef"
      class="absolute pointer-events-none bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 text-sm z-50"
    >
      <div class="font-bold mb-2 border-bottom border-gray-100 dark:border-gray-700 pb-1">
        {{ tooltipData.title }}
      </div>
      <div class="space-y-1">
        <div
          v-for="item in tooltipData.items"
          :key="item.name"
          class="flex items-center justify-between gap-4"
        >
          <div class="flex items-center gap-2">
            <span class="w-3 h-3 rounded-full" :style="{ backgroundColor: item.color }"></span>
            <span class="text-gray-600 dark:text-gray-400">{{ item.name }}:</span>
          </div>
          <span class="font-mono font-medium text-gray-900 dark:text-white">{{ item.value }}</span>
        </div>
      </div>
    </div>
    <div
      v-if="!dataSource || dataSource.length === 0"
      class="flex items-center justify-center h-full text-gray-400"
    >
      No data available
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, onUnmounted } from 'vue'
import * as d3 from 'd3'

interface SeriesConfig {
  valueField: string
  name: string
  color?: string
}

const props = withDefaults(
  defineProps<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataSource: any[]
    argumentField: string
    barSeries: SeriesConfig[]
    lineSeries: SeriesConfig[]
    title?: string
    subtitle?: string
    barAxisTitle?: string
    lineAxisTitle?: string
    barValueFormatter?: (val: number) => string
    lineValueFormatter?: (val: number) => string
    showLabels?: boolean
  }>(),
  {
    title: '',
    subtitle: '',
    barAxisTitle: '',
    lineAxisTitle: '',
    barValueFormatter: (val: number) => new Intl.NumberFormat('de-DE').format(val),
    lineValueFormatter: (val: number) => {
      if (Math.abs(val) < 0.01) return '0 €'
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val)
    },
    showLabels: true,
  },
)

const chartContainer = ref<HTMLElement | null>(null)
const svgRef = ref<SVGSVGElement | null>(null)
const tooltipRef = ref<HTMLElement | null>(null)
const tooltipVisible = ref(false)
const tooltipData = ref({
  title: '',
  items: [] as { name: string; value: string; color: string }[],
})

const hiddenSeries = ref(new Set<string>())

const toggleSeries = (valueField: string) => {
  if (hiddenSeries.value.has(valueField)) {
    hiddenSeries.value.delete(valueField)
  } else {
    hiddenSeries.value.add(valueField)
  }
  drawChart()
}

const margin = { top: 40, right: 100, bottom: 80, left: 100 }


const drawChart = () => {
  if (!svgRef.value || !chartContainer.value || !props.dataSource.length) return

  const width = chartContainer.value.clientWidth - margin.left - margin.right
  const height = chartContainer.value.clientHeight - margin.top - margin.bottom

  if (width <= 0 || height <= 0) return

  // Clear previous chart
  const svg = d3.select(svgRef.value)
  svg.selectAll('*').remove()

  const g = svg
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom)
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`)

  // X scale
  const x0 = d3
    .scaleBand()
    .domain(props.dataSource.map((d) => d[props.argumentField]))
    .rangeRound([0, width])
    .paddingInner(0.1)

  const activeBarSeries = props.barSeries.filter((s) => !hiddenSeries.value.has(s.valueField))
  const activeLineSeries = props.lineSeries.filter((s) => !hiddenSeries.value.has(s.valueField))

  const x1 = d3
    .scaleBand()
    .domain(activeBarSeries.map((s) => s.valueField))
    .rangeRound([0, x0.bandwidth()])
    .padding(0.05)

  // Y scales
  const yLeftMax =
    d3.max(props.dataSource, (d) => d3.max(activeBarSeries, (s) => d[s.valueField])) || 0
  const yLeftMin =
    d3.min(props.dataSource, (d) => d3.min(activeBarSeries, (s) => d[s.valueField])) || 0
  const yRightMax =
    d3.max(props.dataSource, (d) => d3.max(activeLineSeries, (s) => d[s.valueField])) || 0
  const yRightMin =
    d3.min(props.dataSource, (d) => d3.min(activeLineSeries, (s) => d[s.valueField])) || 0

  // Sync Y-axes: Ensure 0 is at the same level by balancing intervals
  const getDomainInfo = (min: number, max: number, tickCount: number) => {
    const rawMin = Math.min(0, min)
    const rawMax = Math.max(0, max) || 10

    // Use d3.ticks to get "nice" intervals
    const ticks = d3.ticks(rawMin, rawMax, tickCount)
    const step = ticks[1] - ticks[0]

    // Find how many steps are needed below and above zero
    const stepsBelow = Math.ceil(Math.abs(rawMin) / step)
    const stepsAbove = Math.ceil(rawMax / step)

    return { stepsBelow, stepsAbove, step }
  }

  const leftInfo = getDomainInfo(yLeftMin, yLeftMax, 5)
  const rightInfo = getDomainInfo(yRightMin, yRightMax, 5)

  // Use the maximum number of steps required by either axis to ensure alignment
  const finalStepsBelow = Math.max(leftInfo.stepsBelow, rightInfo.stepsBelow)
  const finalStepsAbove = Math.max(leftInfo.stepsAbove, rightInfo.stepsAbove)

  const leftDomain: [number, number] = [
    -(finalStepsBelow * leftInfo.step),
    finalStepsAbove * leftInfo.step,
  ]
  const rightDomain: [number, number] = [
    -(finalStepsBelow * rightInfo.step),
    finalStepsAbove * rightInfo.step,
  ]

  const yLeft = d3.scaleLinear().domain(leftDomain).rangeRound([height, 0])
  const yRight = d3.scaleLinear().domain(rightDomain).rangeRound([height, 0])

  // X Axis
  g.append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0,${yLeft(0)})`)
    .call(d3.axisBottom(x0))
    .selectAll('text')
    .attr('transform', `translate(0, ${height - yLeft(0)}) rotate(-15)`)
    .style('text-anchor', 'end')
    .attr('dx', '-.8em')
    .attr('dy', '.15em')
    .style('font-size', '11px')
    .attr('class', 'fill-gray-500 dark:fill-gray-400')

  g.selectAll('.x-axis path, .x-axis line')
    .attr('stroke', '#e5e7eb')
    .attr('class', 'dark:stroke-gray-700')

  // Left Y Axis
  if (activeBarSeries.length > 0) {
    g.append('g')
      .attr('class', 'y-axis-left')
      .call(
        d3
          .axisLeft(yLeft)
          .ticks(4)
          .tickFormat((d) => props.barValueFormatter(d as number)),
      )
      .selectAll('text')
      .style('font-size', '11px')
      .attr('class', 'fill-gray-500 dark:fill-gray-400')

    g.selectAll('.y-axis-left path, .y-axis-left line')
      .attr('stroke', '#e5e7eb')
      .attr('class', 'dark:stroke-gray-700')

    if (props.barAxisTitle) {
      g.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('y', 0 - margin.left + 20)
        .attr('x', 0 - height / 2)
        .attr('dy', '1em')
        .style('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('font-weight', '600')
        .attr('class', 'fill-gray-400 dark:fill-gray-500')
        .text(props.barAxisTitle)
    }
  }

  // Right Y Axis
  if (activeLineSeries.length > 0) {
    g.append('g')
      .attr('class', 'y-axis-right')
      .attr('transform', `translate(${width}, 0)`)
      .call(
        d3
          .axisRight(yRight)
          .ticks(4)
          .tickFormat((d) => props.lineValueFormatter(d as number)),
      )
      .selectAll('text')
      .style('font-size', '11px')
      .attr('class', 'fill-gray-500 dark:fill-gray-400')

    g.selectAll('.y-axis-right path, .y-axis-right line')
      .attr('stroke', '#e5e7eb')
      .attr('class', 'dark:stroke-gray-700')

    if (props.lineAxisTitle) {
      g.append('text')
        .attr('transform', 'rotate(90)')
        .attr('y', -width - margin.right + 20)
        .attr('x', height / 2)
        .attr('dy', '1em')
        .style('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('font-weight', '600')
        .attr('class', 'fill-gray-400 dark:fill-gray-500')
        .text(props.lineAxisTitle)
    }
  }

  // Horizontal Grid Lines
  g.append('g')
    .attr('class', 'grid')
    .call(
      d3
        .axisLeft(yLeft)
        .ticks(4)
        .tickSize(-width)
        .tickFormat(() => ''),
    )
    .selectAll('line')
    .attr('stroke', '#f3f4f6')
    .attr('class', 'dark:stroke-gray-800')

  g.select('.grid path').remove()

  // Bars
  const barsG = g
    .append('g')
    .selectAll('g')
    .data(props.dataSource)
    .join('g')
    .attr('transform', (d) => `translate(${x0(d[props.argumentField])},0)`)

  barsG
    .selectAll('rect')
    .data((d) =>
      activeBarSeries.map((s) => ({
        key: s.valueField,
        value: d[s.valueField],
        color: s.color,
        data: d,
      })),
    )
    .join('rect')
    .attr('x', (d) => x1(d.key)!)
    .attr('y', (d) => (d.value >= 0 ? yLeft(d.value) : yLeft(0)))
    .attr('width', x1.bandwidth())
    .attr('height', (d) => Math.abs(yLeft(d.value) - yLeft(0)))
    .attr('fill', (d) => d.color || '#ccc')
    .attr('rx', 2)
    .attr('ry', 2)

  // Bar Labels
  if (props.showLabels) {
    barsG
      .selectAll('.bar-label')
      .data((d) =>
        activeBarSeries.map((s) => ({ key: s.valueField, value: d[s.valueField], color: s.color })),
      )
      .join('text')
      .attr('class', 'bar-label fill-gray-500 dark:fill-gray-400')
      .attr('x', (d) => x1(d.key)! + x1.bandwidth() / 2)
      .attr('y', (d) => (d.value >= 0 ? yLeft(d.value) - 5 : yLeft(d.value) + 10))
      .attr('text-anchor', 'middle')
      .style('font-size', '10px')
      .style('font-weight', '500')
      .text((d) => (Math.abs(d.value) >= 0.01 ? props.barValueFormatter(d.value) : ''))
  }

  // Lines
   
  const lineGenerator = d3
    .line<{ [key: string]: string | number; value: number }>()
    .x((d) => (x0(String(d[props.argumentField])) || 0) + x0.bandwidth() / 2)
    .y((d) => yRight(d.value))
    .curve(d3.curveMonotoneX)

  activeLineSeries.forEach((s) => {
    const lineData = props.dataSource.map((d) => ({
      [props.argumentField]: d[props.argumentField],
      value: d[s.valueField],
    }))

    g.append('path')
      .datum(lineData)
      .attr('fill', 'none')
      .attr('stroke', s.color || '#000')
      .attr('stroke-width', 3)
      .attr('d', lineGenerator)

    g.selectAll(`.dot-${s.valueField}`)
      .data(lineData)
      .join('circle')
      .attr('class', `dot-${s.valueField}`)
      .attr('cx', (d) => (x0(String(d[props.argumentField])) || 0) + x0.bandwidth() / 2)
      .attr('cy', (d) => yRight(d.value))
      .attr('r', 5)
      .attr('fill', 'white')
      .attr('stroke', s.color || '#000')
      .attr('stroke-width', 2)

    // Line Labels
    if (props.showLabels) {
      g.selectAll(`.label-${s.valueField}`)
        .data(lineData)
        .join('text')
        .attr('class', `label-${s.valueField}`)
        .attr('x', (d) => (x0(String(d[props.argumentField])) || 0) + x0.bandwidth() / 2)
        .attr('y', (d) => (d.value >= 0 ? yRight(d.value) - 12 : yRight(d.value) + 15))
        .attr('text-anchor', 'middle')
        .style('font-size', '10px')
        .style('font-weight', 'bold')
        .attr('fill', s.color || '#000')
        .text((d) => (Math.abs(d.value) >= 0.01 ? props.lineValueFormatter(d.value) : ''))
    }
  })

  // Tooltip Interaction Layer
  const mouseG = g.append('g').attr('class', 'mouse-over-effects')

  const mouseLine = mouseG
    .append('path')
    .attr('class', 'mouse-line dark:stroke-gray-600')
    .style('stroke', '#cbd5e1')
    .style('stroke-width', '1px')
    .style('opacity', '0')

  mouseG
    .append('rect')
    .attr('width', width)
    .attr('height', height)
    .attr('fill', 'none')
    .attr('pointer-events', 'all')
    .on('mouseout', () => {
      tooltipVisible.value = false
      mouseLine.style('opacity', '0')
    })
    .on('mouseover', () => {
      tooltipVisible.value = true
      mouseLine.style('opacity', '1')
    })
    .on('mousemove', (event) => {
      const mouseX = d3.pointer(event)[0]
      const bandwidth = x0.step()
      const index = Math.floor(mouseX / bandwidth)
      const d = props.dataSource[index]

      if (d) {
        const xPos = (x0(d[props.argumentField]) || 0) + x0.bandwidth() / 2
        mouseLine.attr('d', `M${xPos},${height} L${xPos},0`)

        tooltipData.value = {
          title: d[props.argumentField],
          items: [
            ...activeBarSeries.map((s) => ({
              name: s.name,
              value: props.barValueFormatter(d[s.valueField]),
              color: s.color || '#ccc',
            })),
            ...activeLineSeries.map((s) => ({
              name: s.name,
              value: props.lineValueFormatter(d[s.valueField]),
              color: s.color || '#000',
            })),
          ],
        }

        if (tooltipRef.value) {
          const tooltipWidth = tooltipRef.value.offsetWidth
          const tooltipHeight = tooltipRef.value.offsetHeight
          let left = xPos + margin.left + 20
          let top = event.offsetY - tooltipHeight / 2

          if (left + tooltipWidth > width + margin.left + margin.right) {
            left = xPos + margin.left - tooltipWidth - 20
          }

          if (top < 0) top = 10
          if (top + tooltipHeight > height + margin.top)
            top = height + margin.top - tooltipHeight - 10

          tooltipRef.value.style.left = `${left}px`
          tooltipRef.value.style.top = `${top}px`
        }
      }
    })
}

onMounted(() => {
  drawChart()
  window.addEventListener('resize', drawChart)
})

onUnmounted(() => {
  window.removeEventListener('resize', drawChart)
})

watch(() => props.dataSource, drawChart, { deep: true })
watch(() => props.title, drawChart)
watch(() => props.subtitle, drawChart)
</script>

<style scoped>
.d3-chart-container {
  width: 100%;
  height: 100%;
  min-height: 200px;
}
svg {
  width: 100%;
  height: 100%;
}
</style>
